/**
 * DataTable - Editorial Precision Table Component
 *
 * A reusable, accessible data table with sorting, filtering, and responsive design.
 * Uses shadcn/ui Table primitives with editorial styling.
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface Column<T> {
  id: string;
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  getRowId?: (row: T, index: number) => string;
  getRowAccentColor?: (row: T) => string | undefined;
  emptyMessage?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnId: string) => void;
  isLoading?: boolean;
  className?: string;
}

export function DataTable<T>({
  data,
  columns,
  onRowClick,
  getRowId,
  getRowAccentColor,
  emptyMessage = 'No data available',
  sortBy,
  sortDirection,
  onSort,
  isLoading = false,
  className,
}: DataTableProps<T>) {
  const handleSort = (columnId: string, sortable?: boolean) => {
    if (sortable && onSort) {
      onSort(columnId);
    }
  };

  const getCellValue = (row: T, column: Column<T>) => {
    if (column.cell) {
      return column.cell(row);
    }
    if (column.accessorKey) {
      return String(row[column.accessorKey] ?? '');
    }
    return '';
  };

  // Empty state
  if (!isLoading && data.length === 0) {
    return (
      <Card className={cn(
        "rounded-2xl",
        "border border-slate-200/50 dark:border-slate-700/50",
        "bg-white/70 dark:bg-slate-900/70",
        "shadow-[0_16px_40px_rgba(15,23,42,0.08)]",
        "dark:shadow-[0_16px_40px_rgba(0,0,0,0.3)]",
        "backdrop-blur-xl",
        "p-12",
        className
      )}>
        <div className="text-center">
          <p className="text-slate-600 dark:text-slate-400 text-lg">
            {emptyMessage}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn(
      // Frosted glass card wrapper
      "rounded-2xl overflow-hidden",
      "border border-slate-200/50 dark:border-slate-700/50",
      "bg-white/70 dark:bg-slate-900/70",
      "shadow-[0_16px_40px_rgba(15,23,42,0.08)]",
      "dark:shadow-[0_16px_40px_rgba(0,0,0,0.3)]",
      "backdrop-blur-xl",
      // Inner highlight
      "relative",
      "before:content-[''] before:absolute before:inset-x-0 before:top-0 before:h-px",
      "before:bg-gradient-to-r before:from-transparent before:via-white/50 before:to-transparent",
      "dark:before:via-white/10",
      className
    )}>
      <div className="overflow-x-auto">
        <Table className="w-full border-separate border-spacing-y-2">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-0">
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={cn(
                    // Editorial table head styling
                    "font-mono text-xs uppercase tracking-wider",
                    "text-slate-500 dark:text-slate-400",
                    "px-3 py-3",
                    "border-0",
                    column.sortable && "cursor-pointer select-none hover:text-teal-600 dark:hover:text-teal-400",
                    column.width && `w-[${column.width}]`,
                    column.className
                  )}
                  onClick={() => handleSort(column.id, column.sortable)}
                >
                  <div className="flex items-center gap-1.5">
                    {column.header}
                    {column.sortable && sortBy === column.id && (
                      <span className="text-teal-600 dark:text-teal-400">
                        {sortDirection === 'asc' ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </span>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map((row, index) => {
              const rowId = getRowId ? getRowId(row, index) : `row-${index}`;
              const accentColor = getRowAccentColor ? getRowAccentColor(row) : undefined;

              return (
                <TableRow
                  key={rowId}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={cn(
                    // Base styles
                    "bg-white dark:bg-slate-800/50",
                    "border border-slate-200/50 dark:border-slate-700/50",
                    "shadow-[0_10px_24px_rgba(15,23,42,0.06)]",
                    "dark:shadow-[0_10px_24px_rgba(0,0,0,0.2)]",
                    // Accent border
                    accentColor && `border-l-4`,
                    // Transitions
                    "transition-all duration-200 ease-out",
                    // Hover states
                    onRowClick && "cursor-pointer",
                    "hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(15,23,42,0.08)]",
                    "hover:border-teal-500/50 dark:hover:border-teal-400/50",
                    accentColor && "hover:border-l-[5px]",
                    // Active state
                    "active:translate-y-0",
                    // Animations
                    "animate-fade-in animate-slide-up",
                    "motion-reduce:animate-none motion-reduce:hover:transform-none"
                  )}
                  style={{
                    borderLeftColor: accentColor,
                    animationDelay: `${index * 30}ms`,
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell
                      key={`${rowId}-${column.id}`}
                      className={cn(
                        "px-3 py-4",
                        "text-sm text-slate-700 dark:text-slate-300",
                        "border-0",
                        // Rounded corners for first/last cells
                        colIndex === 0 && "rounded-l-xl",
                        colIndex === columns.length - 1 && "rounded-r-xl",
                        column.className
                      )}
                    >
                      {getCellValue(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Loading skeleton overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 dark:border-teal-400" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Loading data...</p>
          </div>
        </div>
      )}
    </Card>
  );
}
